'use client';

import { Button } from '@ahafood/ui/components/button';
import { Card, CardContent, CardHeader } from '@ahafood/ui/components/card';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import {
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTrigger,
} from '@ahafood/ui/components/stepper';
import { Tabs, TabsContent } from '@ahafood/ui/components/tabs';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { getCookie } from 'cookies-next';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  MapPin,
  Package,
  Phone,
  User,
  X,
} from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { LoginDialog } from '../(main)/store/components/fb-login';
import { useGetOrderDetail } from '@/api/use-get-order-detail';
import { useGetOrderHistory } from '@/api/use-get-order-history';
import { ROUTES } from '@/config/router';
// import { useCityRoutes } from '@/hooks/use-city-navigation';
import { Link, usePathname, useRouter } from '@/i18n/routing';
import { DateFormat, dayjs, formatUnixTime } from '@/lib/datetime';
import { trackEvent } from '@/lib/event-tracking';
// import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useAuthStore } from '@/store';

interface OrderNotificationsProps {
  orderId?: string | null;
  onClose?: () => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function OrderNotifications({
  orderId,
  onClose,
}: OrderNotificationsProps = {}) {
  const [activeTab, setActiveTab] = useState('listing');
  const token = String(getCookie('ws_token') ?? '');
  const router = useRouter();
  const isWebinApp = useDetectWebInApp();
  const { user } = useAuthStore();
  // const routes = useCityRoutes();
  // const isMobile = useIsMobile();
  const pathName = usePathname();
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginDialog(false);
    }
  }, [isAuthenticated]);

  // Handle initial orderId - similar to StoreItemSheet logic
  useEffect(() => {
    if (orderId) {
      setSelectedOrder(orderId);
      setActiveTab('detail');
    }
  }, [orderId]);

  const { data: orderHistory, isLoading: historyLoading } = useGetOrderHistory(
    token,
    0,
    20,
    {
      refetchInterval: 5000,
      enabled: activeTab === 'listing',
    }
  );

  // Get order detail (only when order is selected)
  const { data: orderDetail, isLoading: detailLoading } = useGetOrderDetail(
    token,
    selectedOrder!,
    {
      enabled: !!token && !!selectedOrder && activeTab === 'detail',
      refetchInterval: 10000,
    }
  );

  const getStepStatus = (step: number, orderStatus: string) => {
    const statusToStep = {
      CONFIRMING: 1,
      CONFIRMED: 1,
      ASSIGNING: 1, // Looking for driver - step 1 completed

      ACCEPTED: 2, // Driver assigned - step 2 completed
      'IN PROCESS': 3, // Processing - step 2 completed

      BOARDED: 3, // On delivery - step 3 completed

      COMPLETED: 4, // Completed - all steps done

      IN_RETURN: 1,
      RETURNED: 4,
      CANCELLED: 1,
      'BOT CANCELLED': 1,
      FAILED: 1,
      IDLE: 1,
    };

    const currentStep =
      statusToStep[orderStatus as keyof typeof statusToStep] || 1;
    return step <= currentStep;
  };

  const showStepper = orderDetail
    ? !['CANCELLED', 'BOT_CANCELLED', 'FAILED'].includes(orderDetail.status)
    : false;

  const formatTime = (timestamp: number) => {
    return formatUnixTime(timestamp, DateFormat.TIME_24);
  };

  const getStatusInfo = (status: string) => {
    const statusMap = {
      // Trạng thái khởi tạo & xác nhận
      CONFIRMING: {
        text: 'Đang chờ xác nhận',
        description: 'Quán đang xem xét và xác nhận đơn hàng của bạn',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step1.png`}
            alt="Order Success"
            width={30}
            height={30}
            className="object-contain"
          />
        ),
        color: 'text-yellow-60',
      },
      CONFIRMED: {
        text: 'Đã xác nhận',
        description: 'Quán đã xác nhận và đang chuẩn bị món ăn',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step1.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-blue-60',
      },
      // Trạng thái điều phối
      ASSIGNING: {
        text: 'Đang tìm tài xế',
        description: 'Chúng tôi đang tìm tài xế gần nhất để giao đến bạn!',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step1.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-blue-50',
      },
      ACCEPTED: {
        text: 'Tài xế đã nhận',
        description: 'Tài xế đang trên đường đến quán để lấy hàng',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step2.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-blue-50',
      },
      // Trạng thái thực hiện
      'IN PROCESS': {
        text: 'Đang giao đến bạn',
        description: 'Hãy chú ý điện thoại, shipper chuẩn bị giao món đến bạn',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step1.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-green-60',
      },
      BOARDED: {
        text: 'Đang giao đến bạn',
        description: 'Hãy chú ý điện thoại, shipper chuẩn bị giao món đến bạn',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step3.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-primary-50',
      },
      COMPLETED: {
        text: 'Đã hoàn thành',
        description: 'Cảm ơn và chúc bạn ngon miệng!',
        icon: (
          <Image
            src={`${assetPrefix}/static/images/step4.png`}
            alt="Order Success"
            width={80}
            height={80}
            className="object-contain size-20"
          />
        ),
        color: 'text-green-60',
      },
      // Trạng thái hoàn/trả hàng
      IN_RETURN: {
        text: 'Đang hoàn hàng',
        description: 'Đơn hàng đang được hoàn trả',
        icon: <Package className="size-6" />,
        color: 'text-primary-60',
      },
      RETURNED: {
        text: 'Đã hoàn hàng',
        description: 'Đơn hàng đã được hoàn trả thành công',
        icon: <CheckCircle className="size-6" />,
        color: 'text-neutral-60',
      },
      // Trạng thái hủy/thất bại
      CANCELLED: {
        text: 'Đã hủy',
        description: 'Đơn hàng đã bị hủy',
        icon: <X className="size-6" />,
        color: 'text-red-60',
      },
      BOT_CANCELLED: {
        text: 'Hệ thống hủy',
        description: 'Đơn hàng đã bị hủy bởi hệ thống',
        icon: <X className="size-6" />,
        color: 'text-red-60',
      },
      FAILED: {
        text: 'Thất bại',
        description: 'Đơn hàng giao không thành công',
        icon: <X className="size-6" />,
        color: 'text-red-60',
      },
      // Trạng thái khác
      IDLE: {
        text: 'Đang tìm tài xế',
        description: 'Chúng tôi đang tìm tài xế gần nhất để giao đến bạn!',
        icon: <Clock className="size-6" />,
        color: 'text-neutral-60',
      },
    };
    return statusMap[status as keyof typeof statusMap] || statusMap.CONFIRMING;
  };

  const getStepperValue = (status: string) => {
    const statusToStep = {
      CONFIRMING: 1,
      CONFIRMED: 1,
      ASSIGNING: 1, // Looking for driver - step 1 completed

      ACCEPTED: 2, // Driver assigned - step 2 completed
      'IN PROCESS': 3, // Processing - step 2 completed

      BOARDED: 3, // On delivery - step 3 completed

      COMPLETED: 4, // Completed - all steps done

      IN_RETURN: 1,
      RETURNED: 4,
      CANCELLED: 1,
      'BOT CANCELLED': 1,
      FAILED: 1,
      IDLE: 1,
    };

    return statusToStep[status as keyof typeof statusToStep] || 1;
  };

  const handleOrderSelect = (orderCode: string) => {
    trackEvent('click_button', {
      button_name: 'order_detail',
      button_location: 'order_list',
      user_id: user?.cunstomerId,
    });
    setSelectedOrder(orderCode);
    setActiveTab('detail');
  };

  const handleBackToListing = () => {
    if (onClose && orderId) {
      // If we opened with a specific orderId, close the sheet instead of going back to listing
      onClose();
    } else {
      setActiveTab('listing');
      setSelectedOrder(null);
    }
  };

  // const handleClose = () => {
  //   if (isMobile) {
  //     router.push(routes.home);
  //   } else {
  //     onClose?.();
  //   }
  // };

  return (
    <div
      className="max-w-md md:w-full bg-white pb-20"
      // style={{ marginBottom: isNavigationVisible ? bottomPosition : 20 }}
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Header */}
        <div className="bg-secondary-60 text-white p-4 flex items-center justify-between">
          {activeTab === 'detail' ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToListing}
              className="text-white [&_svg]:size-5"
            >
              <ArrowLeft />
            </Button>
          ) : (
            !pathName.includes('order-list') && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-white [&_svg]:size-5"
              >
                <X />
              </Button>
            )
          )}
          <h1 className="text-lg font-medium flex-1 text-center">
            {activeTab === 'listing' ? 'Hoạt động' : 'Chi tiết đơn hàng'}
          </h1>
          {(isWebinApp || activeTab === 'detail') && <div className="w-10" />}
        </div>

        <TabsContent value="listing" className="mt-0">
          <div className="p-4 space-y-3">
            {!isAuthenticated ? (
              // No login state
              <div className="flex flex-col items-center justify-center py-12 text-neutral-40">
                <Image
                  src="/static/images/store-empty.webp"
                  alt="Cần đăng nhập"
                  width={120}
                  height={120}
                  className="mb-4 opacity-80"
                />
                <div className="text-lg font-semibold mb-2">
                  Bạn cần đăng nhập để xem đơn hàng
                </div>
                <div className="text-sm text-center mb-4">
                  Đăng nhập để theo dõi đơn hàng
                </div>
                <Button
                  onClick={() => setShowLoginDialog(true)}
                  className="bg-primary-50 hover:bg-primary-60 text-white px-6 py-2"
                >
                  Đăng nhập
                </Button>
              </div>
            ) : historyLoading ? (
              // Skeleton placeholders for order history list
              <>
                <Skeleton className="h-4 w-24 mb-2" />
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full mb-4 rounded-lg" />
                ))}
              </>
            ) : orderHistory?.orders?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-neutral-40">
                <Image
                  src="/static/images/store-empty.webp"
                  alt="Không có đơn hàng nào"
                  width={120}
                  height={120}
                  className="mb-4 opacity-80"
                />
                <div className="text-lg font-semibold mb-2">
                  Bạn không có đơn nào
                </div>
                <div className="text-sm text-center">
                  Bạn chưa có đơn hàng nào gần đây.
                  <br />
                  Hãy đặt món để thưởng thức ngay!
                </div>
              </div>
            ) : (
              <>
                <div className="text-sm text-neutral-60 mb-4">Hôm nay</div>
                {orderHistory?.orders?.map((order) => {
                  const statusInfo = getStatusInfo(order.status);
                  const isToday = dayjs.unix(order.create_at).isToday();
                  return (
                    <Card
                      key={order.code}
                      className="cursor-pointer hover drop-shadow-md transition-shadow"
                      onClick={() => handleOrderSelect(order.code)}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm text-neutral-60">
                                Đơn: #{order.code}
                              </span>
                            </div>
                            <div className="text-sm font-bold mb-1 text-secondary-40">
                              {order.store_name}
                            </div>
                            <div
                              className={`text-xs font-bold ${statusInfo.color}`}
                            >
                              {statusInfo.text}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-neutral-60 mb-1">
                              {isToday
                                ? `Hôm nay ${formatTime(order.create_at)}`
                                : formatUnixTime(
                                    order.create_at,
                                    DateFormat.DATE_TIME_24
                                  )}
                            </div>
                            <div className="font-bold">
                              {formatCurrency(order.total_payment)}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="detail" className="mt-0">
          {detailLoading ? (
            // Skeleton placeholders for order detail
            <div className="p-4 space-y-4">
              <Skeleton className="h-8 w-1/2 mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6 mb-2" />
              <Skeleton className="h-40 w-full rounded-lg mb-4" />
              <Skeleton className="h-6 w-32 mb-2" />
              <Skeleton className="h-6 w-64 mb-2" />
              <Skeleton className="h-8 w-full rounded-lg" />
            </div>
          ) : orderDetail ? (
            <div className="space-y-4">
              {/* Header with Status and Progress */}
              <div className="bg-white p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-3 rounded-full ${getStatusInfo(orderDetail.status).color}`}
                  >
                    {getStatusInfo(orderDetail.status).icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-lg">
                      {getStatusInfo(orderDetail.status).text}
                    </h3>
                    <p className="text-sm text-neutral-60">
                      {getStatusInfo(orderDetail.status).description}
                    </p>
                  </div>
                </div>
                {/* Progress Stepper */}
                {showStepper && (
                  <div className="w-full px-4">
                    <Stepper
                      value={getStepperValue(orderDetail.status)}
                      orientation="horizontal"
                      className="w-full"
                    >
                      <StepperItem
                        step={1}
                        className="flex-1"
                        completed={getStepStatus(1, orderDetail.status)}
                      >
                        <StepperTrigger className="flex flex-col items-center gap-2">
                          <StepperIndicator className="size-8 flex items-center justify-center">
                            <Image
                              src={`${assetPrefix}/static/images/step1.png`}
                              alt="Order Confirmed"
                              width={24}
                              height={24}
                              className="object-contain"
                            />
                          </StepperIndicator>
                        </StepperTrigger>
                        <StepperSeparator />
                      </StepperItem>
                      <StepperItem
                        step={2}
                        className="flex-1"
                        completed={getStepStatus(2, orderDetail.status)}
                        loading={orderDetail.status === 'ASSIGNING'}
                      >
                        <StepperTrigger className="flex flex-col items-center gap-2">
                          <StepperIndicator className="size-8 flex items-center justify-center">
                            <Image
                              src={`${assetPrefix}/static/images/step2.png`}
                              alt="Driver Assigned"
                              width={24}
                              height={24}
                              className="object-contain"
                            />
                          </StepperIndicator>
                        </StepperTrigger>
                        <StepperSeparator />
                      </StepperItem>
                      <StepperItem
                        step={3}
                        className="flex-1"
                        completed={getStepStatus(3, orderDetail.status)}
                        loading={orderDetail.status === 'BOARDED'}
                      >
                        <StepperTrigger className="flex flex-col items-center gap-2">
                          <StepperIndicator className="size-8 flex items-center justify-center">
                            <Image
                              src={`${assetPrefix}/static/images/step3.png`}
                              alt="On Delivery"
                              width={24}
                              height={24}
                              className="object-contain"
                            />
                          </StepperIndicator>
                        </StepperTrigger>
                        <StepperSeparator />
                      </StepperItem>
                      <StepperItem
                        step={4}
                        className="w-fit"
                        completed={getStepStatus(4, orderDetail.status)}
                      >
                        <StepperTrigger className="flex flex-col items-center gap-2">
                          <StepperIndicator className="size-8 flex items-center justify-center">
                            <Image
                              src={`${assetPrefix}/static/images/step4.png`}
                              alt="Completed"
                              width={24}
                              height={24}
                              className="object-contain"
                            />
                          </StepperIndicator>
                        </StepperTrigger>
                      </StepperItem>
                    </Stepper>
                  </div>
                )}
              </div>
              <div className="text-xs text-neutral-50 p-4">
                {formatUnixTime(
                  orderDetail.order_time,
                  DateFormat.DATE_TIME_24
                )}{' '}
                - Mã đơn: {orderDetail.code}
              </div>

              {/* Store Info */}
              <Card
                onClick={() => {
                  router.push(ROUTES.storeDetail(orderDetail.slug));
                  onClose?.();
                }}
                className="mx-4 border border-neutral-10 drop-shadow-md"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="size-10 bg-neutral-20 rounded-full flex items-center justify-center">
                      <span className="text-lg">🍽️</span>
                    </div>
                    <div>
                      <div className="font-medium text-lg">
                        {orderDetail.store_name}
                      </div>
                      <div className="text-sm text-green-60">
                        {orderDetail.delivery_order_id}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Delivery Address */}
              {/* <Card className="mx-4 border border-neutral-10 drop-shadow-md">
                <CardContent className="p-4 flex flex-col gap-2">
                  <div className="flex items-center gap-2 mb-1 w-full justify-between">
                    <User className="size-5 text-neutral-60" />
                    <h4 className="font-medium">{orderDetail.receiver.name}</h4>
                  </div>
                  <div className="flex items-center gap-2 mb-1 w-full justify-between">
                    <div className="flex items-center gap-2">
                      <Phone className="size-4 text-neutral-60" />
                      <span className="text-sm font-medium">Liên hệ</span>
                    </div>
                    <div className="text-sm">{orderDetail.receiver.mobile}</div>
                  </div>
                  <div className="flex items-start gap-2 mb-1 w-full justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="size-4 text-neutral-60" />
                      <span className="text-sm font-medium">Địa chỉ</span>
                    </div>
                    <div className="text-sm line-clamp-2 max-w-60 text-right">
                      {orderDetail.receiver.address}
                    </div>
                  </div>
                </CardContent>
              </Card> */}
              <Card className="mx-4 border border-neutral-10 drop-shadow-md">
                <CardHeader className="pb-3 p-4 border-b">
                  <h4 className="font-medium">Thông tin người nhận</h4>
                </CardHeader>
                <CardContent className="p-0">
                  <div>
                    <div className="flex items-center gap-3 p-3 rounded-lg">
                      <div className="p-2 rounded-full">
                        <User className="size-5" />
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium block">Tên</span>
                        <h4 className="text-sm">{orderDetail.receiver.name}</h4>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 rounded-lg">
                      <div className="p-2 rounded-full">
                        <Phone className="size-4" />
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium block">
                          Số điện thoại
                        </span>
                        <span className="text-sm">
                          {orderDetail.receiver.mobile}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 rounded-lg">
                      <div className="p-2 rounded-full mt-1">
                        <MapPin className="size-4" />
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium block mb-1">
                          Địa chỉ giao hàng
                        </span>
                        <p className="text-sm leading-relaxed">
                          {orderDetail.receiver.address}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Order Summary */}
              <Card className="mx-4 border border-neutral-10 drop-shadow-md">
                <CardHeader className="pb-3 p-4 border-b">
                  <h4 className="font-medium">Tóm tắt đơn</h4>
                </CardHeader>
                <CardContent className="p-4">
                  {orderDetail.items.map((item) => (
                    <div key={item.id} className="flex flex-col">
                      <div className="flex justify-between text-sm">
                        <span>
                          {item.quantity}x {item.name}
                        </span>
                        <span>
                          {formatCurrency(item.price * item.quantity)}
                        </span>
                      </div>
                      {item.customizations && (
                        <div className="text-xs text-neutral-60 mt-1">
                          {item.customizations.map((customization) => (
                            <div
                              key={customization.id}
                              className="flex items-center justify-between gap-1 pl-2"
                            >
                              {customization.quantity}x{' '}
                              {customization.option_name}
                              <span>{formatCurrency(customization.price)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {item.note && (
                        <div className="text-xs text-neutral-60 mt-1 italic">
                          {item.note}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="mx-4 border border-neutral-10 drop-shadow-md">
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Tạm tính</span>
                      <span className="font-semibold">
                        {formatCurrency(orderDetail.item_fee)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>
                        Phí ship{' '}
                        {orderDetail.distance > 0 &&
                          `(${orderDetail.distance} km)`}
                      </span>
                      <span className="font-semibold">
                        {formatCurrency(orderDetail.delivery_fee)}
                      </span>
                    </div>
                    {orderDetail.applied_promo?.some(
                      (promo) =>
                        promo.name?.toLowerCase() === 'amori' &&
                        promo.scope?.toUpperCase() === 'PRIVATE'
                    ) ? (
                      <div className="flex items-center gap-2 text-sm text-blue-50 rounded-md">
                        <Image
                          src="/static/images/new-bot-avt.webp"
                          alt="Amorii Freeship"
                          width={24}
                          height={24}
                          className="size-6 object-contain shrink-0"
                        />
                        <span>Freeship từ Amori tối đa 30K</span>
                        <span className="ml-auto font-bold text-blue-50">
                          -{formatCurrency(orderDetail.delivery_discount)}
                        </span>
                      </div>
                    ) : orderDetail.delivery_discount > 0 ? (
                      <div className="flex justify-between text-sm text-red-60">
                        <span>Giảm giá vận chuyển</span>
                        <span className="font-semibold text-primary-50">
                          -{formatCurrency(orderDetail.delivery_discount)}
                        </span>
                      </div>
                    ) : null}
                    {orderDetail.applied_promo &&
                      orderDetail.applied_promo.length > 0 &&
                      orderDetail.applied_promo.map(
                        (promo) =>
                          promo.name?.toLowerCase() === 'amori' &&
                          promo.scope?.toUpperCase() === 'PRIVATE' ? null : (
                            <div
                              key={promo.promo_code}
                              className="flex justify-between text-sm text-red-60"
                            >
                              <span>{promo.name}</span>
                              <span className="font-semibold text-primary-50">
                                -{formatCurrency(promo.total_discount_value)}
                              </span>
                            </div>
                          )
                        // <div
                        //   key={promo.promo_code}
                        //   className="flex justify-between text-sm text-red-60"
                        // >
                        //   <span>{promo.name}</span>
                        //   <span className="font-semibold text-primary-50">
                        //     -{formatCurrency(promo.total_discount_value)}
                        //   </span>
                        // </div>
                      )}
                    {orderDetail.merchant_discount > 0 && (
                      <div className="flex justify-between text-sm text-red-60">
                        <span>Giảm giá từ quán</span>
                        <span className="font-semibold text-primary-50">
                          -{formatCurrency(orderDetail.merchant_discount)}
                        </span>
                      </div>
                    )}
                    {orderDetail.platform_discount > 0 && (
                      <div className="flex justify-between text-sm text-red-60">
                        <span>Giảm giá platform</span>
                        <span className="font-semibold text-primary-50">
                          -{formatCurrency(orderDetail.platform_discount)}
                        </span>
                      </div>
                    )}

                    <div className="border-t pt-2">
                      <div className="flex justify-between font-medium">
                        <span>Tổng cộng</span>
                        <span className="font-semibold">
                          {formatCurrency(orderDetail.total_payment)}
                        </span>
                      </div>
                      <div className="text-xs text-neutral-60 mt-1">
                        Thanh toán khi nhận hàng
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Button */}
              <div className="p-4">
                <Link href={orderDetail.tracking_link} target="_blank">
                  <Button className="w-full bg-primary-50 hover:bg-primary-60 text-white py-3">
                    Theo dõi hành trình đơn hàng
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="p-4 text-center">
              <p>Không tìm thấy thông tin đơn hàng</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="amori-login"
      />
    </div>
  );
}
